import { AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useAllTeachers } from '@entities/student/api/useAllTeachers';
import {
	DEFAULT_TEACHER_MATCHING_DELAY_MS,
	getMatchedTeachers,
	getSelectedInstrumentIds,
	getSelectedInstrumentValue,
	getStartWhenApiParams,
	getTeacherMatchingProgress,
	getTeacherPreferenceLabels,
} from './lib/teacher-matching-progress';
import { MatchingHeader } from './ui/matching-header';
import { MatchingProgressBody } from './ui/matching-progress-body';
import { MatchedTeachersResult } from './ui/matched-teachers-result';
import s from './teacher-matching-progress.module.scss';

export const TeacherMatchingProgress = ({profile}) => {
	const [progress, setProgress] = useState(() => getTeacherMatchingProgress(profile))
	const [skipMatching, setSkipMatching] = useState(false)
	const studentPreferences = useMemo(() => profile?.student_preferences || {}, [profile?.student_preferences])
	const selectedInstrument = useMemo(
		() => getSelectedInstrumentValue({ studentPreferences, profile }),
		[profile, studentPreferences]
	)
	const selectedInstrumentIds = useMemo(
		() => getSelectedInstrumentIds({ studentPreferences, profile }),
		[profile, studentPreferences]
	)
	const teacherSearchParams = useMemo(() => {
		const startWhenParams = getStartWhenApiParams(studentPreferences);

		return {
			instruments: selectedInstrumentIds,
			page: 1,
			...(startWhenParams.days_type.length ? { days_type: startWhenParams.days_type } : {}),
			...(startWhenParams.time_of_day.length ? { time_of_day: startWhenParams.time_of_day } : {}),
		};
	}, [selectedInstrumentIds, studentPreferences])
	const {data: teachers, isFetched: teachersIsFetched} = useAllTeachers(teacherSearchParams)
	const isCompleted = progress >= 100 || skipMatching || teachersIsFetched
	const { levelLabel, startWhenLabel } = useMemo(
		() => getTeacherPreferenceLabels({ selectedInstrument, studentPreferences }),
		[selectedInstrument, studentPreferences]
	)
	const matchedTeachers = useMemo(() => getMatchedTeachers(teachers), [teachers])

	useEffect(() => {
		if (skipMatching || teachersIsFetched) return

		const interval = setInterval(() => {
			setProgress((currentProgress) => Math.min(currentProgress + 1, 100))
		}, DEFAULT_TEACHER_MATCHING_DELAY_MS / 100)

		setProgress(getTeacherMatchingProgress(profile))

		return () => clearInterval(interval)
	}, [profile, skipMatching, teachersIsFetched])

	return (
		<>
			<MatchingHeader {...{profile, isCompleted, levelLabel, startWhenLabel, matchedCount: matchedTeachers.length}}/>
			<div className={classNames(s.matchingCard, isCompleted ? s.isCompleted : '')}>
				<AnimatePresence mode="wait" initial={false}>
					{isCompleted ? (
						<MatchedTeachersResult
						teachers={matchedTeachers}
						teachersIsFetched={teachersIsFetched}
						searchParams={teacherSearchParams}
					/>
					) : (
						<MatchingProgressBody progress={progress} onSkip={() => setSkipMatching(true)}/>
					)}
				</AnimatePresence>
			</div>
		</>
	)
}
